/*  Colega Link · Admin · Transferencias manuales (Mediación & Delegación)
    Ruta: /app/admin/transferencias
    - Lista pagos manuales pendientes de validación en Mediaciones y Delegaciones.
    - Permite ver el comprobante, aprobar (aplica la transición del objeto) o rechazar.
*/
import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import {
  Landmark, RefreshCw, Loader2, Check, XCircle, User, Mail, Calendar,
  Coins, Sparkles, ShieldAlert, Paperclip, HeartHandshake, Briefcase,
} from "lucide-react";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const KIND_LABEL = {
  initial: "Mediación · Pago inicial",
  session_additional: "Mediación · Sesión adicional",
  closing_agreement: "Mediación · Cierre con acuerdo",
};

export default function AdminManualTransfers() {
  const [items, setItems] = useState({ mediation_payments: [], delegation_payments: [] });
  const [tab, setTab] = useState("pending_admin_validation"); // pending_admin_validation | paid | canceled
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState({ type: "", text: "" });
  const [reject, setReject] = useState(null); // { collection, payment }
  const [receiptModal, setReceiptModal] = useState(null); // { collection, payment_id, name, mime, b64 }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/manual-transfers?status=${tab}`);
      setItems({
        mediation_payments: r.data?.mediation_payments || [],
        delegation_payments: r.data?.delegation_payments || [],
      });
    } catch (err) {
      setFlash({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const flashTimeout = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash({ type: "", text: "" }), 6000);
  };

  const approve = async (collection, payment) => {
    const amount = payment.amount_clp || payment.total_charged_clp || 0;
    const who = payment.user_name || payment.user_email || "usuario";
    if (!window.confirm(`Confirmar transferencia de ${CLP(amount)} de ${who}?\n\nEsto liberará el flujo del objeto (Mediación o Delegación).`)) return;
    setBusy(payment.id);
    try {
      await api.post(`/admin/manual-transfers/${collection}/${payment.id}/approve`);
      flashTimeout({ type: "success", text: `✓ Pago aprobado.` });
      load();
    } catch (err) {
      flashTimeout({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setBusy(""); }
  };

  const submitReject = async (reason) => {
    const { collection, payment } = reject;
    setBusy(payment.id);
    try {
      await api.post(`/admin/manual-transfers/${collection}/${payment.id}/reject`, { reason });
      flashTimeout({ type: "success", text: "Comprobante rechazado. El usuario podrá reintentar." });
      setReject(null);
      load();
    } catch (err) {
      flashTimeout({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setBusy(""); }
  };

  const openReceipt = async (collection, payment_id) => {
    try {
      const r = await api.get(`/admin/manual-transfers/${collection}/${payment_id}/receipt`);
      setReceiptModal({ collection, payment_id, ...r.data });
    } catch (err) {
      flashTimeout({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const totalPending =
    (items.mediation_payments || []).reduce((s, p) => s + (p.amount_clp || p.total_charged_clp || 0), 0)
    + (items.delegation_payments || []).reduce((s, p) => s + (p.amount_clp || p.total_charged_clp || 0), 0);
  const totalCount = (items.mediation_payments?.length || 0) + (items.delegation_payments?.length || 0);

  return (
    <div className="container-app py-10 max-w-6xl" data-testid="admin-manual-transfers">
      <AdminNav />
      <header className="flex items-center gap-3 mb-6">
        <Landmark size={26} className="text-emerald-300" />
        <div>
          <h1 className="serif text-3xl leading-tight">Transferencias manuales · Mediación & Delegación</h1>
          <p className="text-sm text-muted-foreground">
            Valida los comprobantes que suben Solicitantes y Abogados. Al aprobar, se libera el flujo del objeto (mediación en curso, delegación en escrow, etc.).
          </p>
        </div>
      </header>

      <div className="flex gap-1 mb-5 border-b border-white/5">
        {[
          { k: "pending_admin_validation", label: "Pendientes", tone: "amber" },
          { k: "paid", label: "Aprobados", tone: "emerald" },
          { k: "canceled", label: "Rechazados", tone: "red" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.k ? `border-${t.tone}-400 text-${t.tone}-200` : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          data-testid="refresh-btn"
          disabled={loading}
        >
          {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Actualizar
        </button>
      </div>

      {flash.text && (
        <div className={`mb-4 p-3 rounded-md text-sm ${flash.type === "success" ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-200" : "bg-red-400/10 border border-red-400/30 text-red-200"}`} data-testid="flash">
          {flash.text}
        </div>
      )}

      {tab === "pending_admin_validation" && totalCount > 0 && (
        <div className="mb-4 p-3 rounded-md border border-amber-400/25 bg-amber-400/5 text-sm flex items-center justify-between">
          <span><ShieldAlert size={14} className="inline mr-1 text-amber-300"/> {totalCount} pago{totalCount === 1 ? "" : "s"} pendiente{totalCount === 1 ? "" : "s"} de validación</span>
          <span className="text-amber-300 font-semibold">Total: {CLP(totalPending)}</span>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : totalCount === 0 ? (
        <div className="py-16 text-center panel">
          <Sparkles size={22} className="text-muted-foreground/50 mx-auto"/>
          <div className="mt-3 text-sm text-muted-foreground">Sin resultados en esta pestaña.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {items.mediation_payments.length > 0 && (
            <section>
              <div className="label-eyebrow text-cyan-300 mb-2 inline-flex items-center gap-1.5">
                <HeartHandshake size={12}/> Mediaciones ({items.mediation_payments.length})
              </div>
              <div className="space-y-3">
                {items.mediation_payments.map((p) => (
                  <TransferCard
                    key={p.id} payment={p} collection="mediation_payments"
                    subjectLabel={KIND_LABEL[p.kind] || p.kind}
                    detail={p.subject ? `${p.subject} · ${p.mediator_name || ""}` : ""}
                    tab={tab} busy={busy === p.id}
                    onApprove={() => approve("mediation_payments", p)}
                    onReject={() => setReject({ collection: "mediation_payments", payment: p })}
                    onViewReceipt={() => openReceipt("mediation_payments", p.id)}
                  />
                ))}
              </div>
            </section>
          )}
          {items.delegation_payments.length > 0 && (
            <section>
              <div className="label-eyebrow text-amber-300 mb-2 inline-flex items-center gap-1.5">
                <Briefcase size={12}/> Delegaciones ({items.delegation_payments.length})
              </div>
              <div className="space-y-3">
                {items.delegation_payments.map((p) => (
                  <TransferCard
                    key={p.id} payment={p} collection="payments"
                    subjectLabel="Delegación · Pago Protegido"
                    detail={p.subject ? `${p.subject}${p.assigned_to_name ? ` · Asignada a ${p.assigned_to_name}` : ""}` : ""}
                    tab={tab} busy={busy === p.id}
                    onApprove={() => approve("payments", p)}
                    onReject={() => setReject({ collection: "payments", payment: p })}
                    onViewReceipt={() => openReceipt("payments", p.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {reject && (
        <RejectModal
          payment={reject.payment}
          onClose={() => setReject(null)}
          onSubmit={submitReject}
          busy={busy === reject.payment.id}
        />
      )}
      {receiptModal && (
        <ReceiptModal data={receiptModal} onClose={() => setReceiptModal(null)}/>
      )}
    </div>
  );
}

function TransferCard({ payment, collection, subjectLabel, detail, tab, busy, onApprove, onReject, onViewReceipt }) {
  const amount = payment.amount_clp || payment.total_charged_clp || 0;
  const created = payment.created_at ? new Date(payment.created_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const uploaded = payment.receipt_uploaded_at ? new Date(payment.receipt_uploaded_at).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className="panel p-4 sm:p-5" data-testid={`transfer-${collection}-${payment.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-cyan-400/15 text-cyan-300 border border-cyan-400/30">
              {subjectLabel}
            </span>
            <span className="text-[10px] text-muted-foreground font-mono">Ref {payment.id.slice(0,8).toUpperCase()}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <User size={14} className="text-muted-foreground"/>
            <span className="font-medium">{payment.user_name || "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
            <Mail size={12}/> <a href={`mailto:${payment.user_email}`} className="hover:text-foreground underline decoration-dotted">{payment.user_email || "s/e"}</a>
          </div>
          {detail && <div className="mt-1 text-xs text-muted-foreground italic">{detail}</div>}
          <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar size={11}/> Registrado: {created}</span>
            {uploaded && <span className="inline-flex items-center gap-1 text-cyan-300"><Paperclip size={11}/> Comprobante subido: {uploaded}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="inline-flex items-center gap-1 text-emerald-300 serif text-2xl font-semibold">
            <Coins size={16}/> {CLP(amount)}
          </div>
          {payment.commission_clp > 0 && (
            <div className="text-[10px] text-muted-foreground mt-0.5">Comisión Colega Link: {CLP(payment.commission_clp)}</div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {payment.receipt_uploaded_at && (
          <button onClick={onViewReceipt} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid={`view-receipt-${payment.id}`}>
            <Paperclip size={12}/> Ver comprobante
          </button>
        )}
        {tab === "pending_admin_validation" && (
          <>
            <button
              onClick={onApprove}
              disabled={busy}
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`approve-${payment.id}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Aprobar y liberar flujo
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              className="btn-ghost text-xs inline-flex items-center gap-1.5 text-red-300 hover:text-red-200"
              data-testid={`reject-${payment.id}`}
            >
              <XCircle size={12}/> Rechazar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function RejectModal({ payment, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="reject-modal">
      <div className="panel max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="serif text-xl">Rechazar comprobante</h3>
        <p className="text-xs text-muted-foreground mt-1">Ref {payment.id.slice(0,8).toUpperCase()} · {CLP(payment.amount_clp || payment.total_charged_clp || 0)}</p>
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="field mt-4 w-full text-sm"
          placeholder="Motivo (ej. comprobante ilegible, monto no coincide, cuenta destino incorrecta)"
          data-testid="reject-reason"
        />
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => onSubmit(reason.trim() || "comprobante rechazado por admin")}
            disabled={busy}
            className="btn-primary flex-1 disabled:opacity-50"
            data-testid="reject-confirm"
          >
            {busy ? "Rechazando…" : "Rechazar"}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function ReceiptModal({ data, onClose }) {
  const mime = data.receipt_mime || "application/octet-stream";
  const b64 = data.receipt_b64 || "";
  const dataUrl = `data:${mime};base64,${b64}`;
  const isImg = mime.startsWith("image/");
  const isPdf = mime === "application/pdf";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="receipt-modal">
      <div className="panel max-w-3xl w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="label-eyebrow text-cyan-300">Comprobante</div>
            <div className="text-xs text-muted-foreground">{data.receipt_name}</div>
          </div>
          <div className="flex gap-2">
            <a
              href={dataUrl}
              download={data.receipt_name || "comprobante"}
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              data-testid="receipt-download"
            >Descargar</a>
            <button onClick={onClose} className="btn-ghost text-xs">Cerrar</button>
          </div>
        </div>
        {isImg && <img src={dataUrl} alt="comprobante" className="max-h-[75vh] w-full object-contain rounded bg-black/40" data-testid="receipt-img"/>}
        {isPdf && <iframe title="comprobante" src={dataUrl} className="w-full h-[75vh] bg-white rounded" data-testid="receipt-pdf"/>}
        {!isImg && !isPdf && <div className="text-sm text-muted-foreground">Archivo binario. Usa &quot;Descargar&quot; para revisarlo.</div>}
      </div>
    </div>
  );
}
